import { formatTime } from "@/i18n";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import type { ConversationMessage } from "@/stores/conversation-store";
import type { InputAgent } from "@/components/panels/agent-stack";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import {
  CONVERSATION_FRAME_HEIGHT,
  CONVERSATION_FRAME_WIDTH,
} from "@/lib/conversation-frame-surface";
import type { FrameData } from "@/lib/frame-data";

export function conversationFrameNativeGroup(
  frameId: string,
  detached: boolean,
): string {
  // `frame:*` is reserved for React Flow nodes whose native bounds come from
  // the canvas model. Detached cursor chats have no model node and must use
  // their DOM bounds instead.
  return detached ? `detached-frame:${frameId}` : `frame:${frameId}`;
}

export function metadataString(
  metadata: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return undefined;
}

/** Task a conversation frame belongs to: the ticket link, else the task id
 *  the runtime stamped into metadata. */
export function conversationFrameTaskId(
  frame: Pick<FrameData, "taskTicket" | "metadata">,
): string | undefined {
  return (
    frame.taskTicket?.id ?? metadataString(frame.metadata, "taskId", "task_id")
  );
}

export function metadataNumber(
  metadata: Record<string, unknown>,
  ...keys: string[]
): number | undefined {
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return undefined;
}

export function conversationFrameContextMetrics(
  metadata: Record<string, unknown>,
) {
  return {
    persistedContextPercent: metadataNumber(
      metadata,
      "contextPercent",
      "context_percent",
    ),
    persistedContextUsedTokens:
      metadata.contextUsedTokens === null
        ? null
        : metadataNumber(metadata, "contextUsedTokens"),
    persistedContextWindowMax: metadataNumber(metadata, "contextWindowMax"),
  };
}

/** A message's time of day in the app language; "" for a missing time. */
export function formatMessageTime(iso: string): string {
  const time = Date.parse(iso);
  return Number.isFinite(time)
    ? formatTime(time, { hour: "2-digit", minute: "2-digit" })
    : "";
}

export function currentFrameCenter(frameId: string): { x: number; y: number } {
  const node = useCanvasStore.getState().nodes.find((n) => n.id === frameId);
  const { width, height } = node
    ? frameNodeSize(node, {
        width: CONVERSATION_FRAME_WIDTH,
        height: CONVERSATION_FRAME_HEIGHT,
      })
    : { width: CONVERSATION_FRAME_WIDTH, height: CONVERSATION_FRAME_HEIGHT };
  return {
    x: (node?.position.x ?? 0) + width / 2,
    y: (node?.position.y ?? 0) + height / 2,
  };
}

export function agentKeyForInputAgent(agent: InputAgent): string {
  return agent.name.toLowerCase();
}

export function findInputAgentByKey(
  agents: InputAgent[],
  agentKey: string | undefined,
): InputAgent | null {
  if (!agentKey) return null;
  return (
    agents.find((agent) => agentKeyForInputAgent(agent) === agentKey) ?? null
  );
}

export function resolveConversationFrameTargetAgent(args: {
  text: string;
  agents: InputAgent[];
  selectedAgentIds: Set<string>;
  frameAgent: InputAgent | null;
}): InputAgent | null {
  return findInputAgentByKey(args.agents, "denker");
}

export function sameGeometry(
  a: { x: number; width: number },
  b: { x: number; width: number },
): boolean {
  return Math.abs(a.x - b.x) <= 0.5 && Math.abs(a.width - b.width) <= 0.5;
}

export function isFocusPresentationFrame(frameId: string): boolean {
  const presentation = usePresentationStore.getState().presentation;
  return (
    presentation?.variant === "focus" && presentation.frameIds.includes(frameId)
  );
}

export function isMessageVisibleInConversationFrame(
  message: ConversationMessage,
  frame: {
    conversationId?: string;
    id: string;
    executionId?: string;
    runtimeSessionId?: string;
    requireExactIdentity?: boolean;
  },
): boolean {
  if (frame.conversationId) {
    if (message.conversationId) {
      return frame.conversationId === message.conversationId;
    }
    // Live cursor/tool updates can be bound to the visible frame before the
    // persisted message carries its durable conversation identity.
    if (!frame.requireExactIdentity && message.conversationFrameId === frame.id)
      return true;
    if (frame.runtimeSessionId && message.sessionId) {
      return frame.runtimeSessionId === message.sessionId;
    }
    if (frame.executionId && message.executionId) {
      return frame.executionId === message.executionId;
    }
    return false;
  }
  // Unbound frames only accept an explicit frame binding. Runtime identity is
  // supplemental to a durable Conversation and must never select chat history.
  if (message.conversationFrameId === frame.id) return true;
  return false;
}
